import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight, Menu, RefreshCw, Search, ShoppingBag, Truck, User, X } from 'lucide-react';
import { trackPixelEvent } from '../utils/pixel';
import { Logo } from './icons/Logo';
import { AdminCategory, db, MenuItem, StoreConfig } from '../services/backend';
import { useCart } from '../contexts/CartContext';
import { dedupeMenuItems } from '../utils/menuDefaults';

interface HeaderProps {
  config?: StoreConfig['topbar'];
  theme?: 'white' | 'dark' | 'auto';
  categories?: AdminCategory[];
}

interface PromoMessage {
  text: string;
  link?: {
    label: string;
    url: string;
  };
}

const DEFAULT_PROMO_MESSAGES = [
  'Frete grátis para todo o Brasil em compras acima de R$99',
  'Garanta sua compra com parcelamento em até 12x sem juros',
  'Central de ajuda para dúvidas sobre pedidos',
];

const TRENDING_SEARCHES = [
  'body splash',
  'mousse facial',
  'skincare',
  'perfumaria',
  'kits',
  'body cream',
  'make',
  'roll-on',
  'hair',
  'bem-estar',
];

const FALLBACK_MAIN_NAV_ITEMS: MenuItem[] = [
  { id: 'fallback-home', label: 'inicio', url: '/', type: 'header', order: 0 },
  { id: 'fallback-products', label: 'produtos', url: '/produtos', type: 'header', order: 1 },
  { id: 'fallback-blog', label: 'blog', url: '/blog', type: 'header', order: 2 },
];

const FALLBACK_UTILITY_LINKS: Array<{ id: string; label: string; url: string }> = [
  { id: 'fallback-rastreio', label: 'Rastreio', url: '/rastreio' },
  { id: 'fallback-trocas', label: 'Trocar e Devolver', url: '/trocas' },
];

const normalizeText = (value?: string | null) =>
  (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const isTrackingLabel = (label?: string | null) => {
  const normalizedLabel = normalizeText(label);
  return normalizedLabel.includes('rastreio') || normalizedLabel.includes('rastreamento');
};

const isReturnsLabel = (label?: string | null) => {
  const normalizedLabel = normalizeText(label);
  return (
    normalizedLabel.includes('troca') ||
    normalizedLabel.includes('devol') ||
    normalizedLabel.includes('devolver')
  );
};

const resolveCategoryUrl = (category: AdminCategory) => {
  const normalizedSlug = normalizeText(category.slug);
  const normalizedName = normalizeText(category.name);

  if (normalizedSlug === 'equipamentos' || normalizedName === 'equipamentos') {
    return '/equipamentos';
  }

  if (normalizedSlug.includes('kit') || normalizedName.includes('kit')) {
    return '/kits';
  }

  return `/produtos/${category.slug}`;
};

const TopPromoBar: React.FC<{
  enabled?: boolean;
  animated?: boolean;
  backgroundColor?: string;
  textColor?: string;
  isScrolled?: boolean;
  messages?: PromoMessage[];
}> = ({
  enabled = true,
  animated = true,
  backgroundColor = 'var(--color-brand-primary)',
  textColor = 'var(--color-brand-on-primary)',
  isScrolled = false,
  messages = [],
}) => {
  const promoMessages =
    messages.length > 0 ? messages : DEFAULT_PROMO_MESSAGES.map((text) => ({ text }));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (!animated || promoMessages.length <= 1) {
      setCurrentIndex(0);
      setIsVisible(true);
      return;
    }

    const interval = setInterval(() => {
      setIsVisible(false);
      const timeout = setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % promoMessages.length);
        setIsVisible(true);
      }, 220);

      return () => clearTimeout(timeout);
    }, 4000);

    return () => clearInterval(interval);
  }, [animated, promoMessages.length]);

  if (!enabled) return null;

  const currentMessage = promoMessages[currentIndex] || promoMessages[0];

  const renderMessage = (message: PromoMessage) => {
    if (!message.link?.label || !message.text.includes(message.link.label)) {
      if (message.link) {
        return (
          <Link to={message.link.url} className="underline underline-offset-2 hover:opacity-80">
            {message.text}
          </Link>
        );
      }

      return message.text;
    }

    const [before, after] = message.text.split(message.link.label, 2);

    return (
      <>
        {before}
        <Link to={message.link.url} className="underline underline-offset-2 hover:opacity-80">
          {message.link.label}
        </Link>
        {after}
      </>
    );
  };

  return (
    <div
      className={`relative z-[61] w-full overflow-hidden transition-all duration-300 ${
        isScrolled ? 'shadow-[inset_0_-1px_0_rgba(255,255,255,0.08)]' : ''
      }`}
      style={{ backgroundColor, color: textColor }}
    >
      <div className="mx-auto flex h-[52px] max-w-[1512px] items-center justify-center px-4">
        <p
          className={`text-center text-[12px] font-semibold tracking-[0.01em] transition-opacity duration-300 sm:text-[15px] ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {currentMessage ? renderMessage(currentMessage) : null}
        </p>
      </div>
    </div>
  );
};

const SearchDropdown: React.FC<{
  isOpen: boolean;
  onSelect: (term: string) => void;
  searchQuery: string;
}> = ({ isOpen, onSelect, searchQuery }) => {
  if (!isOpen) return null;

  const filteredTerms = searchQuery.trim()
    ? TRENDING_SEARCHES.filter((term) => term.toLowerCase().includes(searchQuery.toLowerCase()))
    : TRENDING_SEARCHES;

  return (
    <div className="absolute left-0 right-0 top-full z-50 overflow-hidden rounded-b-lg border border-veraz-border bg-white shadow-lg animate-slideDown">
      <div className="px-5 pb-1 pt-4">
        <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.2em] text-veraz-gray">
          Termos mais buscados
        </p>
      </div>
      <ul className="pb-3">
        {filteredTerms.map((term, index) => (
          <li key={term}>
            <button
              type="button"
              onClick={() => onSelect(term)}
              className="flex w-full items-center gap-3 px-5 py-2.5 text-left text-[14px] text-veraz-gray transition-colors duration-150 hover:bg-gray-50 hover:text-veraz-pink"
            >
              <span className="w-5 text-right text-[11px] font-semibold text-gray-300">
                {index + 1}º
              </span>
              <span>{term}</span>
            </button>
          </li>
        ))}
        {filteredTerms.length === 0 && (
          <li className="px-5 py-3 text-sm italic text-gray-400">Nenhum resultado encontrado</li>
        )}
      </ul>
    </div>
  );
};

const HeaderMain: React.FC<{
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  handleSearch: (event: React.FormEvent) => void;
  cartCount: number;
  isGlass?: boolean;
  isScrolled?: boolean;
  utilityLinks: Array<{ id: string; label: string; url: string }>;
}> = ({
  searchQuery,
  setSearchQuery,
  handleSearch,
  cartCount,
  isGlass = false,
  isScrolled = false,
  utilityLinks,
}) => {
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectTerm = (term: string) => {
    setSearchQuery(term);
    setIsSearchFocused(false);
  };

  return (
    <div
      className={`relative z-[60] w-full transition-all duration-300 ${
        isGlass
          ? 'bg-[#f7f7f7]/64 backdrop-blur-2xl supports-[backdrop-filter]:bg-[#f7f7f7]/54 group-hover/veraz-header:bg-[#f7f7f7] group-hover/veraz-header:shadow-none group-hover/veraz-header:backdrop-blur-none'
          : isScrolled
            ? 'bg-[#f7f7f7] shadow-sm'
            : 'bg-[#f7f7f7]'
      }`}
    >
      <div className="mx-auto max-w-[1512px] px-6 xl:px-8">
        <div className="hidden h-[82px] items-center gap-7 md:flex xl:gap-9">
          <Link to="/" className="shrink-0">
            <Logo
              aria-label="Veraz Cosméticos"
              className="h-auto w-[180px] text-veraz-pink transition-opacity hover:opacity-80 xl:w-[195px]"
            />
          </Link>

          <div ref={searchRef} className="relative max-w-[875px] flex-1">
            <form onSubmit={handleSearch} className="relative">
              <Search
                size={22}
                className="pointer-events-none absolute left-5 top-1/2 -translate-y-1/2 text-veraz-pink"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                placeholder="digite aqui o que procura..."
                aria-label="Buscar produtos"
                className={`h-[50px] w-full rounded-[14px] border bg-white pl-14 pr-5 text-[15px] font-normal text-gray-700 outline-none transition-all duration-200 placeholder:text-[#8e8e8e] ${
                  isSearchFocused
                    ? 'border-veraz-pink shadow-sm'
                    : 'border-[#ececec] hover:border-[#d8d8d8]'
                }`}
              />
            </form>
            <SearchDropdown
              isOpen={isSearchFocused}
              onSelect={handleSelectTerm}
              searchQuery={searchQuery}
            />
          </div>

          <div className="flex shrink-0 items-center gap-6 text-[#202020]">
            {utilityLinks.map((link) => (
              <Link
                key={link.id}
                to={link.url}
                className="hidden items-center gap-2 text-[16px] font-normal transition-colors duration-200 hover:text-veraz-pink lg:flex"
              >
                {isTrackingLabel(link.label) ? <Truck size={18} strokeWidth={1.9} /> : null}
                {isReturnsLabel(link.label) ? <RefreshCw size={17} strokeWidth={1.9} /> : null}
                <span>{link.label}</span>
              </Link>
            ))}

            <Link
              to="/minha-conta"
              aria-label="Minha conta"
              className="flex items-center transition-colors duration-200 hover:text-veraz-pink"
            >
              <User size={23} strokeWidth={1.9} />
            </Link>
            <Link
              to="/carrinho"
              aria-label="Carrinho"
              className="relative transition-colors duration-200 hover:text-veraz-pink"
            >
              <ShoppingBag size={24} strokeWidth={1.9} />
              {cartCount > 0 && (
                <span className="absolute -right-2 -top-2 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-veraz-pink text-[9px] font-bold text-white">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const MainNav: React.FC<{
  items: MenuItem[];
  locationPath: string;
  isGlass?: boolean;
  isScrolled?: boolean;
}> = ({ items, locationPath, isGlass = false, isScrolled = false }) => {
  return (
    <div
      className={`relative z-[59] hidden w-full transition-all duration-300 md:block ${
        isGlass
          ? 'bg-[#f7f7f7]/64 backdrop-blur-2xl supports-[backdrop-filter]:bg-[#f7f7f7]/54 group-hover/veraz-header:bg-[#f7f7f7] group-hover/veraz-header:shadow-none group-hover/veraz-header:backdrop-blur-none'
          : isScrolled
            ? 'bg-[#f7f7f7] shadow-[0_8px_18px_rgba(35,31,32,0.05)]'
            : 'bg-[#f7f7f7]'
      }`}
    >
      <div className="mx-auto max-w-[1512px] px-6 xl:px-8">
        <nav
          className="flex items-center gap-9 overflow-x-auto py-[17px]"
          aria-label="Navegação principal"
        >
          {items.map((item) => {
            const isActive =
              locationPath === item.url || (item.url !== '/' && locationPath.startsWith(item.url));

            return (
              <Link
                key={item.id}
                to={item.url}
                className={`whitespace-nowrap text-[17px] transition-colors duration-200 ${
                  isActive
                    ? 'font-medium text-veraz-pink'
                    : 'font-normal text-[#202020] hover:text-veraz-pink'
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

const MobileHeader: React.FC<{
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  handleSearch: (event: React.FormEvent) => void;
  cartCount: number;
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (open: boolean) => void;
  navItems: MenuItem[];
  utilityLinks: Array<{ id: string; label: string; url: string }>;
  locationPath: string;
}> = ({
  searchQuery,
  setSearchQuery,
  handleSearch,
  cartCount,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  navItems,
  utilityLinks,
  locationPath,
}) => {
  return (
    <>
      <div className="relative z-[60] w-full bg-[#f7f7f7] md:hidden">
        <div className="grid h-[56px] grid-cols-[44px_1fr_44px] items-center px-4">
          <button
            className="flex h-11 w-11 items-center justify-center text-veraz-gray transition-colors hover:text-veraz-pink"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <Link to="/" className="flex justify-self-center">
            <Logo aria-label="Veraz Cosméticos" className="block h-auto w-[118px] text-veraz-pink" />
          </Link>

          <Link
            to="/carrinho"
            className="relative flex h-11 w-11 items-center justify-center justify-self-end"
            aria-label="Carrinho"
          >
            <ShoppingBag size={22} className="text-veraz-gray" />
            {cartCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-[16px] w-[16px] items-center justify-center rounded-full bg-veraz-pink text-[9px] font-bold text-white">
                {cartCount}
              </span>
            )}
          </Link>
        </div>

        <div className="px-4 pb-3">
          <form onSubmit={handleSearch} className="relative">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-veraz-pink"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="digite aqui o que procura..."
              aria-label="Buscar produtos"
              className="h-11 w-full rounded-[14px] border border-[#ececec] bg-white pl-11 pr-4 text-[14px] text-gray-700 outline-none transition-all focus:border-veraz-pink"
            />
          </form>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="absolute left-0 right-0 top-full z-[200] h-[calc(100dvh-100%)] overflow-y-auto bg-white shadow-[0_24px_48px_rgba(35,31,32,0.08)] md:hidden">
          <div className="flex min-h-full flex-col bg-white">
            <div className="hidden h-[56px] items-center justify-between border-b border-veraz-border px-4">
              <Logo aria-label="Veraz Cosméticos" className="h-auto w-[100px] text-veraz-pink" />
              <button
                className="p-2 text-veraz-gray"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Fechar menu"
              >
                <X size={24} />
              </button>
            </div>

            <nav className="py-2">
              {navItems.map((item) => (
                <div key={item.id}>
                  <Link
                    to={item.url}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      window.scrollTo(0, 0);
                    }}
                    className={`flex items-center justify-between px-6 py-4 text-[15px] transition-colors ${
                      locationPath === item.url
                        ? 'font-medium text-veraz-pink'
                        : 'text-veraz-gray hover:text-veraz-pink'
                    }`}
                  >
                    <span>{item.label}</span>
                    <ChevronRight size={14} className="opacity-30" />
                  </Link>
                  <div className="mx-6 border-b border-gray-50" />
                </div>
              ))}
            </nav>

            <div
              className="mt-4 space-y-3 border-t border-veraz-border px-6 pt-4"
              style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))' }}
            >
              <Link
                to="/minha-conta"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-3 py-2 text-[14px] text-veraz-gray transition-colors hover:text-veraz-pink"
              >
                <User size={16} /> minha conta
              </Link>
              {utilityLinks.map((link) => (
                <Link
                  key={link.id}
                  to={link.url}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 py-2 text-[14px] text-veraz-gray transition-colors hover:text-veraz-pink"
                >
                  {isTrackingLabel(link.label) ? <Truck size={16} /> : null}
                  {isReturnsLabel(link.label) ? <RefreshCw size={14} /> : null}
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Header: React.FC<HeaderProps> = ({ config, categories = [] }) => {
  const { cartCount } = useCart();
  const location = useLocation();
  const navigate = useNavigate();

  const [menus, setMenus] = useState<MenuItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;

    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const loadMenus = async () => {
      try {
        const menuItems = await db.getMenus();
        setMenus((menuItems || []).sort((left, right) => left.order - right.order));
      } catch {
        console.warn('Header: Could not load menus from Firestore, using fallback.');
      }
    };

    loadMenus();
  }, []);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    if (!searchQuery.trim()) return;

    trackPixelEvent('Search', { search_string: searchQuery });
    navigate(`/produtos?q=${encodeURIComponent(searchQuery)}`);
    setSearchQuery('');
  };

  const headerMenuItems = useMemo(
    () =>
      dedupeMenuItems(
        menus
          .filter((item) => (item.type === 'header' || item.type === 'both') && !item.parentId)
          .sort((left, right) => left.order - right.order),
        'header'
      ),
    [menus]
  );

  const categoryMenuItems = useMemo(
    () =>
      categories
        .filter((category) => {
          if (category.parentId) return false;
          const normalizedName = normalizeText(category.name);
          const normalizedSlug = normalizeText(category.slug);

          return (
            normalizedName !== 'todos' &&
            normalizedSlug !== 'todos' &&
            normalizedName !== 'blog' &&
            normalizedSlug !== 'blog'
          );
        })
        .sort((left, right) => (left.order || 0) - (right.order || 0))
        .map(
          (category, index) =>
            ({
              id: `category-${category.id}`,
              label: category.name.toLowerCase(),
              url: resolveCategoryUrl(category),
              type: 'header',
              order: index,
            }) as MenuItem
        ),
    [categories]
  );

  const mainNavItems = useMemo(() => {
    const baseItems = headerMenuItems.length > 0 ? headerMenuItems : FALLBACK_MAIN_NAV_ITEMS;
    const mergedItems = [...baseItems];
    const existingUrls = new Set(baseItems.map((item) => item.url));

    categoryMenuItems.forEach((item) => {
      if (existingUrls.has(item.url)) return;
      mergedItems.push({
        ...item,
        order: mergedItems.length,
      });
    });

    const filteredItems = mergedItems.filter((item) => {
      const normalizedLabel = normalizeText(item.label);
      return normalizedLabel !== 'inicio' && normalizedLabel !== 'home' && item.url !== '/';
    });

    return filteredItems.length > 0 ? filteredItems : mergedItems;
  }, [categoryMenuItems, headerMenuItems]);

  const utilityLinks = useMemo(() => FALLBACK_UTILITY_LINKS, []);
  const isTopbarEnabled = config?.enabled ?? true;
  const isGlassHeader = isScrolled && !isHeaderHovered && !isMobileMenuOpen;
  const spacerClass = isTopbarEnabled ? 'h-[164px] md:h-[192px]' : 'h-[112px] md:h-[140px]';

  const promoMessages: PromoMessage[] = useMemo(() => {
    const topbarText = config?.text?.trim();
    const topbarLinks =
      config?.links?.filter((link) => link?.label?.trim() && link?.url?.trim()) || [];

    if (topbarText && topbarLinks.length > 0) {
      return [
        {
          text: topbarText,
          link: topbarLinks[0],
        },
      ];
    }

    if (topbarText) {
      return [{ text: topbarText }];
    }

    if (topbarLinks.length > 0) {
      return topbarLinks.map((link) => ({
        text: link.label,
        link,
      }));
    }

    return DEFAULT_PROMO_MESSAGES.map((text) => ({ text }));
  }, [config]);

  return (
    <>
      <header
        className="group/veraz-header fixed inset-x-0 top-0 z-[110] font-sans transition-all duration-300"
        onMouseEnter={() => setIsHeaderHovered(true)}
        onMouseLeave={() => setIsHeaderHovered(false)}
        onPointerEnter={() => setIsHeaderHovered(true)}
        onPointerLeave={() => setIsHeaderHovered(false)}
      >
        <TopPromoBar
          enabled={isTopbarEnabled}
          animated={config?.animated ?? true}
          backgroundColor={config?.backgroundColor || 'var(--color-brand-primary)'}
          textColor={config?.textColor || 'var(--color-brand-on-primary)'}
          messages={promoMessages}
          isScrolled={isScrolled}
        />

        <HeaderMain
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleSearch={handleSearch}
          cartCount={cartCount}
          isGlass={isGlassHeader}
          isScrolled={isScrolled}
          utilityLinks={utilityLinks}
        />

        <MainNav
          items={mainNavItems}
          locationPath={location.pathname}
          isGlass={isGlassHeader}
          isScrolled={isScrolled}
        />

        <MobileHeader
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleSearch={handleSearch}
          cartCount={cartCount}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          navItems={mainNavItems}
          utilityLinks={utilityLinks}
          locationPath={location.pathname}
        />
      </header>
      <div className={spacerClass} aria-hidden="true" />
    </>
  );
};

export default Header;
